import { validateCustomer, normalizeCustomerPayload, customerApiErrors } from "../validation";
import { ValidationErrorsContext } from "../../../shared/components/FormType/ValidationErrorsContext";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../../shared/hooks/api";
import BackButton from "../../../shared/components/BackButton";
import Title from "./Title";
import TabNavigation from "./TabBar";
import { Customer } from "../../../data/entities";
import { success, error } from "../../../utils/toast";
import PageLoader from "../../../shared/components/LoadingPage";
import { PageContentWrapper } from "../../../shared/styled/PageContentStyle";
import { WarningUnsavedChanges } from "../../../configs/warningProductModal";
import CancelModal from "../../../shared/components/CancelModal";
import {
    getDeliveryAddressesValidationMessage,
    serializeDeliveryAddresses,
} from "./deliveryAddresses";
import {
    mergeCustomerResponse,
    normalizeCustomerData,
} from "./customerData";

const CustomerDetail = () => {
    const { customerId } = useParams();
    const navigate = useNavigate();
    const [isModified, setIsModified] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");
    const [validated, setValidated] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const clearError = (key: string) => setFieldErrors(current => {
        if (!(key in current)) return current;
        const next = { ...current };
        delete next[key];
        return next;
    });
    const [{ data, isLoading, setLocalData }] = api.get(
        `/customers/${customerId}/`
    );
    const customer = useMemo(() => normalizeCustomerData(data), [data]);
    const [{ isDeleting }, deleteCustomer] = api.delete(
        `/customers/${customerId}/`
    );
    const [{ isUpdating }, updateCustomer] = api.patch(
        `/customers/${customerId}/`
    );

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (isModified) {
                event.preventDefault();
                event.returnValue = "";
                return "";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    const markAsModified = useCallback(() => {
        setIsModified(true);
    }, []);

    const handleDeleteCustomer = async () => {
        try {
            await deleteCustomer();
            success("THÀNH CÔNG", "Xóa khách hàng thành công");
        } catch (err) {
            error("Lỗi xóa khách hàng", err);
        }
    };

    const handleSubmitCustomer = async () => {
        setValidated(true);
        const errors = validateCustomer(customer);
        setFieldErrors(errors);
        if (Object.keys(errors).length) return false;

        const deliveryAddresses = customer.delivery_addresses || [];
        const deliveryAddressError = getDeliveryAddressesValidationMessage(
            deliveryAddresses,
        );
        if (deliveryAddressError) {
            setValidated(true);
            error("Lỗi địa chỉ giao hàng", {
                delivery_addresses: [deliveryAddressError],
            });
            return false;
        }

        try {
            const updatedCustomer = {
                ...customer,
                delivery_addresses: serializeDeliveryAddresses(deliveryAddresses),
                sales_staff:
                    customer.sales_staff &&
                    typeof customer.sales_staff === "object"
                        ? (customer.sales_staff as any).id || null
                        : customer.sales_staff,
                // Convert products to products_data format for backend
                products_data: (customer as any).products?.map((product: any) => ({
                    id: product.id,
                    price: product.price
                })) || [],
            };

            // Remove the read-only products field before sending
            delete (updatedCustomer as any).products;

            const savedCustomer = (await updateCustomer(
                normalizeCustomerPayload(updatedCustomer)
            )) as Customer;
            setLocalData(mergeCustomerResponse(customer, savedCustomer));
            setValidated(false);
            setIsModified(false);
            success("THÀNH CÔNG", "Cập nhật khách hàng thành công");
            return true;
        } catch (err) {
            setFieldErrors(customerApiErrors(err));
            error("Lỗi cập nhật khách hàng", err);
            return false;
        }
    };
    
    const confirmNavigation = useCallback((path: string) => {
        if (isModified) {
            setShowUnsavedDialog(true);
            setNavigationPath(path);
            return false;
        } 

        navigate(path);
        return true;
    }, [isModified, navigate]);

    const handleBackClick = () => {
        confirmNavigation("/customers");
    };

    const handleSaveAndNavigate = async () => {
        setShowUnsavedDialog(false);
        try {
            const saved = await handleSubmitCustomer();
            if (saved) {
                navigate(navigationPath);
            }
        } catch (err) {
            error("Lỗi cập nhật khách hàng", err);
        }
    };

    const handleDontSaveAndNavigate = () => {
        setShowUnsavedDialog(false);
        navigate(navigationPath);
    };

    const handleDataChange = (updateCustomer: any) => {
        markAsModified();
        setLocalData({
            ...customer,
            ...updateCustomer,
        });
    };
    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };

    return (
        <ValidationErrorsContext.Provider value={{ errors: fieldErrors, clearError }}>
        <PageContentWrapper>
            <BackButton label="Trở về" onClick={handleBackClick}/>
            {isLoading && <PageLoader />}
            {!isLoading && (
                <Title
                    title={customer.name}
                    setValidated={setValidated}
                    deleteCustomer={handleDeleteCustomer}
                    handleSubmit={handleSubmitCustomer}
                    data={customer}
                    onDataChange={markAsModified}
                    hasUnsavedChanges={isModified}
                    isSaving={isUpdating}
                    onCancelClick={() => confirmNavigation("/customers")}
                />
            )}
            {!isLoading && (
                <TabNavigation
                    validated={validated}
                    customerData={customer}
                    mergeCustomer={handleDataChange}
                />
            )}

            <CancelModal
                open={showUnsavedDialog}
                handleConfirm={handleSaveAndNavigate}
                handleClose={handleDontSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
            />
        </PageContentWrapper>
        </ValidationErrorsContext.Provider>
    );
};

export default CustomerDetail;
